'use client'
/**
 * Renders the App Store buttons component.
 *
 * This component is responsible for rendering the App Store and Google Play buttons.
 * It uses the `useIsSsr` hook to determine if the component is being server-side rendered.
 * If not, it sets the device type based on the user agent.
 *
 * The component then checks if the device is Android and only the Google button link is available,
 * or if the device is iOS and only the Apple button link is available.
 * Depending on these conditions, it renders the appropriate button(s).
 *
 * @component
 * @param {Object} props.appleButton - The Apple App Store button content. This object should contain the link for the Apple App Store.
 * @param {Object} props.googleButton - The Google Play Store button content. This object should contain the link for the Google Play Store.
 * @param {Object} props.appleImage - The Apple App Store button image. This object should contain the image source for the Apple App Store button.
 * @param {Object} props.googleImage - The Google Play Store button image. This object should contain the image source for the Google Play Store button.
 * @param {string} props.navLinkSection - The navigation link section. This is used for tracking purposes. This value should be the app store buttons renderer's section name
 * @returns {JSX.Element} The rendered component. This component will render the App Store and Google Play buttons based on the device type and available button links.
 */
import { AchieveLink } from 'components/AchieveLink'
import useIsSsr from 'hooks/useIsSsr'
import { useEffect, useState } from 'react'
import { actualDevice, DEVICES } from 'utils/shared'
import { LinkButton, MediaImageStandard } from 'components/Contentful'
import { getFeatureFlag } from '@/utils/featureFlags'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
const AchieveSkeleton = dynamic(() => import('components/AchieveSkeleton/AchieveSkeleton'))
import { mainProductConfig } from 'constants/mainProductConfig'

const AppStoreButtons = ({
  appleButton,
  googleButton,
  appleImage,
  googleImage,
  navLinkSection,
  overflow = true,
  priority = false,
}) => {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const [device, setDevice] = useState(null)
  const isSsr = useIsSsr()
  const showSkeleton = getFeatureFlag('acx-good-molo-skeleton-experiment')

  // useEffect to set the device type based on the user agent
  // doesn't run on the server because there is no user agent on the server
  useEffect(() => {
    if (!isSsr) {
      setDevice(actualDevice(navigator.userAgent))
    }
  }, [isSsr])

  // Check if the device is Android and only Google button link is available
  const onlyAndroidNoImage =
    device === DEVICES.android && googleImage == undefined && googleButton?.linkHref != undefined
  // Check if the device is iOS and only Apple button link is available
  const onlyAppleNoImage =
    device === DEVICES.ios && appleImage == undefined && appleButton?.linkHref != undefined

  // If the user is on ios and the ios image is not available, render the apple button as a outlined button without the apple store image
  if (onlyAppleNoImage) {
    return (
      <LinkButton
        target="blank"
        variant="outlined"
        content={appleButton}
        className="mt-8"
        typographicOptions={{ variant: 'bodyLg' }}
      />
    )
  }

  // If the user is on android and the android image is not available, render the google button as a outlined button without the google store image
  if (onlyAndroidNoImage) {
    return (
      <LinkButton
        target="blank"
        variant="outlined"
        content={googleButton}
        className="mt-8"
        typographicOptions={{ variant: 'bodyLg' }}
      />
    )
  }

  // Define the AppleButton component
  // This component will exist if the device is desktop or ios, and the appleImage exists
  const AppleButton = (device === DEVICES.desktop || device === DEVICES.ios) && appleImage && (
    <AchieveLink
      href={appleButton?.linkHref}
      track={{
        ...{
          list_name: 'APPLE APP STORE BUTTON',
          click_id: appleImage?.mediaAltText || 'Apple App Store',
          click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
            appleImage?.mediaAltText || 'Apple App Store'
          }`,
          click_position: 1,
          nav_link_section: navLinkSection,
          event_type: 'button_click',
          click_type: 'Button Click',
          click_url: appleButton?.linkHref,
        },
        ...appleButton.event,
      }}
      data-testid="apple-app-store-btn"
    >
      <div className={classNames('h-[38px] w-[118px] mr-4')} data-testid="apple-button-image">
        <MediaImageStandard
          content={appleImage?.mediaContent || appleImage}
          fetchPriority="high"
          preload={priority}
          alt={appleImage?.mediaAltText || 'apple-button-image'}
        />
      </div>
    </AchieveLink>
  )

  // Define the GoogleButton component
  // This component will exist if the device is desktop or android, and the googleImage exists
  const GoogleButton = (device === DEVICES.desktop || device === DEVICES.android || overflow) &&
    googleImage && (
      <AchieveLink
        href={googleButton?.linkHref}
        track={{
          ...{
            list_name: 'GOOGLE APP STORE BUTTON',
            click_id: googleImage?.mediaAltText || 'Google Play Store',
            click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
              googleImage?.mediaAltText || 'Google Play Store'
            }`,
            click_position: 2,
            nav_link_section: navLinkSection,
            event_type: 'button_click',
            click_type: 'Button Click',
            click_url: googleButton?.linkHref,
          },
          ...googleButton.event,
        }}
        data-testid="google-app-store-btn"
      >
        <div className={classNames('w-[134px] h-[38px]')} data-testid="google-button-image">
          <MediaImageStandard
            content={googleImage?.mediaContent || googleImage}
            fetchPriority="high"
            preload={priority}
            alt={googleImage?.mediaAltText || 'google-button-image'}
          />
        </div>
      </AchieveLink>
    )

  // If neither AppleButton nor GoogleButton exist, return null to avoid rendering an empty Box component
  if (device === null) {
    return (
      <div className={classNames('flex h-[38px]')} data-testid="app-store-btn-container">
        {showSkeleton && <AchieveSkeleton height={38} width={268} variant="transparent" />}
      </div>
    )
  }
  if (!AppleButton && !GoogleButton) return null
  // If either AppleButton or GoogleButton exist, render them inside a Box component with specific styles
  return (
    <div
      className={classNames('flex h-[38px]', overflow ? 'flex flex-row justify-center' : '')}
      data-testid="app-store-btn-container"
    >
      {AppleButton}
      {GoogleButton}
    </div>
  )
}

export default AppStoreButtons
